import { useTranslation } from "react-i18next";
import { Box, Button } from "@mui/material";

import { useNetworkFiltersState } from "../../../hooks/useNetworkFiltersState";
import { useCountryListing } from "../../../../../api/queries/network/useCountryListing";
import { entityGenderEnum, entityVerificationStatusEnum } from "../../../../../enums/entity.enum";

import FilterAutocomplete from "./FilterAutocomplete";

const styles = {
  container: {
    width: "100%",
    overflowX: "auto",
    whiteSpace: "nowrap",
    padding: "0 8px 8px",
  },
  filtersWrapper: {
    display: "flex",
    gap: "10px",
    width: "fit-content",
  },
  autocomplete: {
    "& .MuiAutocomplete-input": {
      right: "3px !important",
    },
  },
  textField: {
    "& .MuiAutocomplete-input": {
      paddingRight: "12px !important",
    },
  },
  advancedFilterButton: {
    color: "#F3BC4E",
    border: `1px solid #F3BC4E`,
    padding: "7.5px 4px 7.5px 5px",
    "&:hover": {
      color: "#F3BC4E",
      border: `1px solid #F3BC4E`,
      backgroundColor: "#FEF8ED",
    },
  },
} as const;

const filterWidths = {
  country: "150px",
  nationality: "220px",
  gender: "120px",
  verification: "150px",
} as const;

const NetworkFilters = ({ isDataLoading }: { isDataLoading: boolean }) => {
  const { t } = useTranslation("profiles", {
    keyPrefix: "network",
  });
  const { filters, updateFilters, clearFilters, hasActiveFilters } = useNetworkFiltersState();
  const { countries, nationalities, isCountriesDataLoading } = useCountryListing();

  const entityGender = [
    { label: "male", value: entityGenderEnum.MALE },
    { label: "female", value: entityGenderEnum.FEMALE },
  ];

  const entityVerificationStatus = [
    { label: "verified", value: entityVerificationStatusEnum.VERIFIED },
    { label: "unverified", value: entityVerificationStatusEnum.UNVERIFIED },
  ];

  const filterConfigs: Array<{
    options: Array<{ label: string; value: string | number }>;
    value: string | undefined;
    filterKey: "location" | "nationality" | "gender" | "verificationStatus" | "limit" | "offset";
    width: string;
    placeholder: string;
    disabled: boolean;
  }> = [
    {
      options: countries || [],
      value: filters.location,
      filterKey: "location",
      width: filterWidths.country,
      placeholder: t("country"),
      disabled: isDataLoading || isCountriesDataLoading,
    },
    {
      options: nationalities || [],
      value: filters.nationality,
      filterKey: "nationality",
      width: filterWidths.nationality,
      placeholder: t("nationality"),
      disabled: isDataLoading || isCountriesDataLoading,
    },
    {
      options: entityGender,
      value: filters.gender,
      filterKey: "gender",
      width: filterWidths.gender,
      placeholder: t("gender"),
      disabled: isDataLoading,
    },
    {
      options: entityVerificationStatus,
      value: filters.verificationStatus,
      filterKey: "verificationStatus",
      width: filterWidths.verification,
      placeholder: t("verified"),
      disabled: isDataLoading,
    },
  ];

  return (
    <Box sx={styles.container}>
      <Box sx={styles.filtersWrapper}>
        {filterConfigs.map((config) => (
          <FilterAutocomplete key={config.filterKey} {...config} updateFilters={updateFilters} />
        ))}

        {hasActiveFilters && (
          <Button disabled={isDataLoading} variant="text-sm" onClick={clearFilters}>
            {t("clear")}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default NetworkFilters;
